import { Store } from "@tanstack/react-store";
import type { ProductV3 } from "../types";
import { localStorage } from "../utils/local-storage";
import { getProducts, storeProduct } from "./all-products";
import { withCustomizations } from "./customizations-store";

// Store product codes in a Set for O(1) lookups
export const favoriteProductsIndexStore = new Store<Set<string>>(new Set());

// Storage key for the favorites index
export const FAVORITES_INDEX_KEY = "favorites_index";

let initialized = false;

// Load favorite products index from localStorage
export const loadFavoriteProductsIndex = (): void => {
  const indexJson = localStorage?.getItem(FAVORITES_INDEX_KEY);
  if (indexJson && indexJson !== "undefined") {
    try {
      const parsedData = JSON.parse(indexJson);
      // Always parse as array, whether it's coming from old object format or array format
      const codes = Array.isArray(parsedData) ? parsedData : Object.keys(parsedData);
      favoriteProductsIndexStore.state = new Set(codes);
      console.log(`Loaded ${codes.length} favorites`);
    } catch (e) {
      console.error("Failed to parse favorites index:", e);
      favoriteProductsIndexStore.state = new Set();
    }
  }

  initialized = true;
};

export const isInitialized = (): boolean => initialized;

// Subscribe to changes in the favorites index and save to localStorage
favoriteProductsIndexStore.subscribe(() => {
  console.log("favoriteProductsIndexStore updated");
  localStorage?.setItem(FAVORITES_INDEX_KEY, JSON.stringify([...favoriteProductsIndexStore.state]));
});

// Get all favorite products as a record for compatibility with existing code
export const getFavoriteProducts = (): Record<string, ProductV3> => {
  const map = getProducts([...favoriteProductsIndexStore.state]);
  for (const code of Object.keys(map)) {
    map[code] = withCustomizations(map[code]);
  }
  return map;
};

// Add a product to favorites
export const addFavoriteProduct = (product: ProductV3): boolean => {
  if (!product.code) return false;

  // First store the product in the main product store
  const stored = storeProduct(product);
  if (!stored) return false;

  // Then add it to the favorites index if not already there
  favoriteProductsIndexStore.setState((prevSet) => new Set([...prevSet, product.code!]));

  return true;
};

// Remove a product from favorites
export const removeFavoriteProduct = (productCode: string): boolean => {
  if (!productCode) return false;

  // Remove from the favorites index
  favoriteProductsIndexStore.setState((prevSet) => {
    const newSet = new Set([...prevSet]);
    newSet.delete(productCode);
    return newSet;
  });

  return true;
};

// Clear all favorite products
export const clearFavoriteProducts = (): void => {
  favoriteProductsIndexStore.setState(() => new Set());
};

// Backward compatibility function
export const loadFavoriteProducts = (): void => {
  // First try to load from the new format
  loadFavoriteProductsIndex();

  // If no favorites were loaded, try to load from old format for migration
  if (favoriteProductsIndexStore.state.size === 0) {
    const oldJson = localStorage?.getItem("favoriteProducts");
    if (oldJson && oldJson !== "undefined") {
      try {
        const oldFavorites = JSON.parse(oldJson) as Record<string, ProductV3>;

        // Migrate data: store products and add to index
        Object.values(oldFavorites).forEach((product) => {
          if (product.code) {
            storeProduct(product);
            favoriteProductsIndexStore.setState((prev) => new Set([...prev, product.code!]));
          }
        });

        // Remove old format after migration
        localStorage?.removeItem("favoriteProducts");

        console.log("Migrated favorite products from old format");
      } catch (e) {
        console.error("Failed to migrate favorite products:", e);
      }
    }
  }
};

export const findFavoriteProducts = (searchTerm: string): ProductV3[] => {
  const products = Object.values(getFavoriteProducts());
  const searchTermLower = (searchTerm || "").toLowerCase();
  return products.filter(
    (product) =>
      product?.product_name?.toLowerCase?.().includes(searchTermLower) ||
      product?.product_name_en?.toLowerCase?.().includes(searchTermLower) ||
      product?.brands?.toLowerCase?.().includes(searchTermLower) ||
      product?.categories_tags?.some?.((category) =>
        typeof category === "string" && category.toLowerCase().includes(searchTermLower)
      ) ||
      product?.food_groups_tags?.some?.((group) =>
        typeof group === "string" && group.toLowerCase().includes(searchTermLower)
      )
  );
};
